import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { isEmpty } from 'lodash';
import { MapPinIcon, PencilSquareIcon, PlusIcon } from '@heroicons/react/24/outline';

// Redux
import { setOrderDataAction } from '../../../redux/Actions/CreateOrderActions';
import { setSelectedServiceAddressAction } from '../../../redux/Actions/ServiceAddressesActions';
import { getOrderPreferencesApi } from '../../../redux/Reducers/MyAccountReducer';

// Components
import ServiceAddressModal from '../PickupAddress/ServiceAddressModal';
import PromoSurpriseModal from '../PromoSurpriseModal';
import { Section, LinkButton, Placeholder, Spinner } from './ui';
import { SECTION_IDS, isAddressDisabled, isOk } from './helpers';

export default function AddressSection() {
  const dispatch = useDispatch();
  const { allServiceAddresses, selectedAddress } = useSelector((s) => s.serviceAddresses);
  const { orderServiceAddress } = useSelector((s) => s.createNewOrder);
  const { profileData } = useSelector((s) => s.profile);

  const [pickerOpen, setPickerOpen] = useState(false);
  const [loadingId, setLoadingId] = useState(null);
  const [modal, setModal] = useState({ open: false, type: 'new', id: null });
  const [promo, setPromo] = useState({ open: false, addressId: null });

  const addresses = [...(allServiceAddresses || [])].sort(
    (a, b) => (isAddressDisabled(a) ? 1 : 0) - (isAddressDisabled(b) ? 1 : 0)
  );
  const canAddAddress = profileData?.billing_type !== 'SEPARATE' && profileData?.is_allowed_to_add_address;

  const selectAddress = (addr, userClick = false) => {
    if (!addr?.id || addr.id === orderServiceAddress?.id) {
      setPickerOpen(false);
      return;
    }
    const address = { id: addr.id, full_address: addr.full_address, name: addr.name, time_zone: addr.time_zone };
    setLoadingId(addr.id);
    dispatch(setSelectedServiceAddressAction(address));
    // Everything downstream is address-specific.
    dispatch(
      setOrderDataAction({
        selectedServices: [],
        orderTimeSlots: null,
        orderIsRush: { is_rush: false },
        orderPaymentDetails: null,
      })
    );
    dispatch(getOrderPreferencesApi(address.id))?.then((res) => {
      setLoadingId(null);
      dispatch(
        setOrderDataAction({
          orderServiceAddress: address,
          orderProvider: (isOk(res) && res?.data?.preferred_worker) || null,
        })
      );
      setPickerOpen(false);
      if (userClick) setPromo({ open: true, addressId: address.id });
    });
  };

  // Auto-select the account's current address, or the first usable one.
  useEffect(() => {
    if (orderServiceAddress || loadingId || isEmpty(addresses)) return;
    const current = addresses.find((a) => a.id === selectedAddress?.id && !isAddressDisabled(a));
    const fallback = addresses.find((a) => !isAddressDisabled(a));
    const pick = current || fallback;
    if (pick) selectAddress(pick);
    else setPickerOpen(true);
  }, [allServiceAddresses, selectedAddress?.id]);

  // No addresses at all — go straight to adding one.
  useEffect(() => {
    if (Array.isArray(allServiceAddresses) && allServiceAddresses.length === 0 && canAddAddress)
      setModal({ open: true, type: 'new', id: null });
  }, [allServiceAddresses]);

  const showPicker = pickerOpen || !orderServiceAddress;
  const selectedFull = addresses.find((a) => a.id === orderServiceAddress?.id);

  // Keep the order's copy of the address in sync after an edit.
  useEffect(() => {
    if (!selectedFull || !orderServiceAddress) return;
    if (
      selectedFull.full_address !== orderServiceAddress.full_address ||
      selectedFull.name !== orderServiceAddress.name
    )
      dispatch(
        setOrderDataAction({
          orderServiceAddress: {
            ...orderServiceAddress,
            full_address: selectedFull.full_address,
            name: selectedFull.name,
          },
        })
      );
  }, [selectedFull?.full_address, selectedFull?.name]);

  return (
    <Section
      id={SECTION_IDS.address}
      step={1}
      title='Pickup address'
      summary={orderServiceAddress ? `${orderServiceAddress.name} · ${orderServiceAddress.full_address}` : null}
      done={!!orderServiceAddress && !showPicker}
      action={
        orderServiceAddress &&
        (addresses.length > 1 || canAddAddress) && (
          <LinkButton onClick={() => setPickerOpen((v) => !v)}>{showPicker ? 'Cancel' : 'Change'}</LinkButton>
        )
      }
    >
      {!showPicker ? (
        <div className='flex items-start gap-3'>
          <div className='bg-client-iconbg flex h-9 w-9 shrink-0 items-center justify-center rounded-md'>
            <MapPinIcon className='text-client-300 h-5 w-5' />
          </div>
          <div className='min-w-0 flex-1'>
            <p className='text-sm font-semibold text-slate-900'>{orderServiceAddress?.name}</p>
            <p className='text-sm text-slate-500'>{orderServiceAddress?.full_address}</p>
          </div>
          {selectedFull && (
            <LinkButton onClick={() => setModal({ open: true, type: 'edit', id: selectedFull.id })}>
              <PencilSquareIcon className='h-3.5 w-3.5' />
              Edit
            </LinkButton>
          )}
        </div>
      ) : isEmpty(addresses) ? (
        canAddAddress ? (
          <button
            type='button'
            onClick={() => setModal({ open: true, type: 'new', id: null })}
            className='bg-client-300 hover:bg-client-600 inline-flex items-center gap-1.5 rounded-md px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition'
          >
            <PlusIcon className='h-4 w-4' />
            Add new service address
          </button>
        ) : (
          <Placeholder>No service addresses yet.</Placeholder>
        )
      ) : (
        <div className='grid grid-cols-1 gap-2 md:grid-cols-2'>
          {addresses.map((addr) => {
            const disabled = isAddressDisabled(addr);
            const selected = orderServiceAddress?.id === addr.id;
            return (
              <div
                key={addr.id}
                role='radio'
                aria-checked={selected}
                aria-disabled={disabled}
                tabIndex={disabled ? -1 : 0}
                onClick={() => !disabled && selectAddress(addr, true)}
                onKeyDown={(e) => {
                  if (disabled || (e.key !== 'Enter' && e.key !== ' ')) return;
                  e.preventDefault();
                  selectAddress(addr, true);
                }}
                className={`flex items-start gap-3 rounded-md border p-3 transition ${
                  disabled
                    ? 'cursor-not-allowed border-slate-100 bg-slate-50 opacity-50'
                    : selected
                    ? 'border-client-300 bg-client-300/5 ring-client-300 cursor-pointer ring-1'
                    : 'cursor-pointer border-slate-200 hover:border-slate-300'
                }`}
              >
                <span
                  className={`mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full border ${
                    selected ? 'border-client-300' : 'border-slate-300'
                  }`}
                >
                  {selected && <span className='bg-client-300 h-2 w-2 rounded-full' />}
                </span>
                <div className='min-w-0 flex-1'>
                  <p className='text-sm font-semibold text-slate-900'>{addr.name}</p>
                  <p className='text-xs leading-snug text-slate-500'>{addr.full_address}</p>
                  {addr.status === 'INACTIVE' ? (
                    <span className='mt-1 inline-block rounded bg-red-50 px-1.5 py-0.5 text-[11px] font-medium text-red-600'>
                      Inactive
                    </span>
                  ) : (
                    !addr.is_address_in_active_service_area && (
                      <span className='mt-1 inline-block rounded bg-amber-50 px-1.5 py-0.5 text-[11px] font-medium text-amber-700'>
                        Not in service area
                      </span>
                    )
                  )}
                </div>
                {loadingId === addr.id ? (
                  <Spinner />
                ) : (
                  !disabled && (
                    <button
                      type='button'
                      aria-label='Edit address'
                      className='hover:text-client-300 rounded p-0.5 text-slate-400'
                      onClick={(e) => {
                        e.stopPropagation();
                        setModal({ open: true, type: 'edit', id: addr.id });
                      }}
                    >
                      <PencilSquareIcon className='h-4 w-4' />
                    </button>
                  )
                )}
              </div>
            );
          })}
          {canAddAddress && (
            <button
              type='button'
              onClick={() => setModal({ open: true, type: 'new', id: null })}
              className='hover:border-client-300 hover:text-client-300 flex min-h-[64px] items-center justify-center gap-1.5 rounded-md border border-dashed border-slate-300 p-3 text-sm font-medium text-slate-500 transition'
            >
              <PlusIcon className='h-4 w-4' />
              Add new address
            </button>
          )}
        </div>
      )}

      <ServiceAddressModal
        newAddressModalOpen={modal.open}
        setNewAddressModalOpen={(v) => setModal((m) => ({ ...m, open: typeof v === 'function' ? v(m.open) : v }))}
        addressModalType={modal.type}
        selectedAddressId={modal.id}
      />
      <PromoSurpriseModal
        addressId={promo.addressId}
        open={promo.open}
        onClose={() => setPromo((p) => ({ ...p, open: false }))}
      />
    </Section>
  );
}
